import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import { usePlanMapOptionsPanel } from "./controller";
import { PlanMapOptionDialog } from "./PlanMapOptionDialog";
import { PlanMapOptionsPanelProps } from "./types";

export function PlanMapOptionsPanel({ integrationId }: PlanMapOptionsPanelProps) {
  const {
    translate,
    options,
    isLoading,
    endpoints,
    dialogOption,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    handleDelete,
    handleSetDefault,
    handleToggleActive,
    createMutation,
    editMutation,
    isMutating,
  } = usePlanMapOptionsPanel({ integrationId });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">
          {translate("erp_integrations.plan_map_options.title")}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreateDialog}>
          {translate("erp_integrations.actions.add_option")}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {translate("erp_integrations.plan_map_options.help")}
      </Typography>

      {isLoading && <CircularProgress size={20} />}

      {!isLoading && options.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {translate("erp_integrations.plan_map_options.empty")}
        </Typography>
      )}

      {!isLoading && options.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{translate("erp_integrations.plan_map_options.label")}</TableCell>
              <TableCell>{translate("erp_integrations.plan_map_options.endpoint")}</TableCell>
              <TableCell>{translate("erp_integrations.plan_map_options.field")}</TableCell>
              <TableCell>{translate("erp_integrations.plan_map_options.field_type")}</TableCell>
              <TableCell align="center">{translate("erp_integrations.plan_map_options.is_default")}</TableCell>
              <TableCell align="center">{translate("erp_integrations.plan_map_options.active")}</TableCell>
              <TableCell align="right">{translate("table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.map((option) => (
              <TableRow key={option.id} hover>
                <TableCell>{option.label}</TableCell>
                <TableCell>{option.endpoint_slug}</TableCell>
                <TableCell>
                  <code>{option.field}</code>
                </TableCell>
                <TableCell>{option.field_type}</TableCell>
                <TableCell align="center">
                  {option.is_default ? (
                    <Chip size="small" color="primary" icon={<StarIcon fontSize="small" />} label={translate("erp_integrations.plan_map_options.is_default")} />
                  ) : (
                    <Tooltip title={translate("erp_integrations.actions.set_default")}>
                      <IconButton size="small" onClick={() => handleSetDefault(option)} disabled={isMutating}>
                        <StarBorderIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={option.active}
                    onChange={() => handleToggleActive(option)}
                    disabled={isMutating}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={translate("erp_integrations.actions.edit_option")}>
                    <IconButton size="small" onClick={() => openEditDialog(option)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate("erp_integrations.actions.delete_option")}>
                    <IconButton size="small" color="error" onClick={() => handleDelete(option)} disabled={isMutating}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PlanMapOptionDialog
        integrationId={integrationId}
        endpoints={endpoints}
        option={dialogOption}
        isSaving={createMutation.isPending || editMutation.isPending}
        onClose={closeDialog}
        onSubmit={(data, existing) => {
          if (existing) {
            editMutation.mutate({ id: existing.id, data });
          } else {
            createMutation.mutate(data);
          }
        }}
      />
    </Box>
  );
}
